import { TRIAL_FOLLOW_UP_DAYS } from '@/shared/constants';
import { dateColumn } from '@/shared/grids/columns/dateColumn';
import { idColumn } from '@/shared/grids/columns/idColumn';
import { numberColumn } from '@/shared/grids/columns/numberColumn';
import { textColumn } from '@/shared/grids/columns/textColumn';
import { GridSettings } from '@/shared/types/grid';
import { classes } from '@/shared/utils/style';

import { AccountView } from '@prisma/client';

export const accountGrid: GridSettings<AccountView> = {
  defaultSort: {
    name: 'asc',
  },
  extraSelect: ['disabled'],
  columns: [
    idColumn(),
    textColumn({
      header: 'Account',
      field: 'name',
      width: 200,
      template: (account: AccountView) =>
        `<span class="${classes(account.disabled && 'text-more-muted text-decoration-line-through')}">${account.name}</span>`,
    }),
    textColumn({
      header: 'Owner',
      field: 'ownerName',
    }),
    textColumn({
      header: 'Owner First Name',
      field: 'ownerFirstName',
      visible: false,
    }),
    textColumn({
      header: 'Owner Last Name',
      field: 'ownerLastName',
      visible: false,
    }),
    textColumn({
      header: 'Owner Email',
      field: 'ownerEmail',
      visible: true,
      template: `<a href="https://secure.helpscout.net/mailbox/7609c22d0de816ac/new-ticket/?email=\${ownerEmail}" target="_blank">\${ownerEmail}</a>`,
    }),
    dateColumn({
      uid: 'lastSeenDate',
      header: 'Last Seen',
      field: 'lastSeenTime',
      format: 'dayMonthYear',
      emptyText: 'Never',
    }),
    dateColumn({
      uid: 'lastSeenTime',
      header: 'At',
      field: 'lastSeenTime',
      format: 'time',
      emptyText: 'Never',
      visible: false,
    }),
    dateColumn({
      header: 'Trial Expires',
      field: 'trialExpiresTime',
      visible: false,
    }),
    numberColumn({
      header: 'Trial Days',
      field: 'trialDaysLeft',
    }),
    textColumn({
      header: 'Last Follow Up',
      field: 'lastTrialFollowUpCode',
      emptyText: 'None',
      template: (account: AccountView) => {
        const { lastTrialFollowUpCode, trialDaysLeft } = account;

        let cssClass: string | undefined;

        if (trialDaysLeft === null || trialDaysLeft < -14) {
          cssClass = 'text-more-muted';
        } else if (!lastTrialFollowUpCode) {
          cssClass = 'text-danger';
        } else if (lastTrialFollowUpCode === 'Skip') {
          cssClass = 'text-more-muted';
        } else {
          const lastTrialFollowUpDay = parseInt(lastTrialFollowUpCode);

          const nextTrialFollowUpDay = TRIAL_FOLLOW_UP_DAYS.find((day) => day < lastTrialFollowUpDay);

          cssClass =
            nextTrialFollowUpDay !== undefined && trialDaysLeft <= nextTrialFollowUpDay
              ? 'text-danger'
              : 'text-success';
        }

        return `<span class="${cssClass}">${lastTrialFollowUpCode ?? 'None'}</span>`;
      },
    }),
    textColumn({
      header: 'Plan',
      field: 'planName',
      emptyText: 'None',
    }),
    textColumn({
      header: 'Features',
      field: 'features',
      visible: false,
    }),
    numberColumn({
      header: 'Fee',
      field: 'subscriptionFee',
    }),
    dateColumn({
      header: 'Sub Created',
      field: 'subscriptionCreatedAt',
      visible: false,
    }),
    dateColumn({
      header: 'Sub Expires',
      field: 'subscriptionExpiresTime',
      visible: false,
    }),
    numberColumn({
      header: 'Sub Days',
      field: 'subscriptionDaysLeft',
    }),
    dateColumn({
      header: 'Sub Cancelled',
      field: 'subscriptionCancelledTime',
      visible: false,
    }),
    numberColumn({
      header: 'Users',
      field: 'userCount',
    }),
    numberColumn({
      header: 'Active (30D)',
      field: 'activeUserCount',
      visible: false,
    }),
    numberColumn({
      header: 'User Limit',
      field: 'userLimit',
      visible: false,
    }),
    numberColumn({
      header: 'Clients',
      field: 'clientCount',
    }),
    numberColumn({
      header: 'Client Limit',
      field: 'companyLimit',
      visible: false,
    }),
    numberColumn({
      header: 'Individuals',
      field: 'individualCount',
    }),
    numberColumn({
      header: 'Individual Limit',
      field: 'individualLimit',
      visible: false,
    }),
    // numberColumn({
    //   header: 'Tasks',
    //   field: 'taskCount',
    //   visible: false,
    // }),
    numberColumn({
      header: 'Tasks',
      field: 'currentTaskCount',
    }),
    numberColumn({
      header: 'Recurring',
      field: 'recurringTaskCount',
    }),
    numberColumn({
      header: 'Smart',
      field: 'smartTaskCount',
    }),
    numberColumn({
      header: 'Created (30D)',
      field: 'recentlyCreatedTaskCount',
      visible: false,
    }),
    numberColumn({
      header: 'Completed (30D)',
      field: 'recentlyCompletedTaskCount',
    }),
    dateColumn({
      header: 'Created',
      field: 'createdTime',
      visible: false,
    }),
  ],
};
